/** First-run onboarding logic — the pure choice→proposed-domain mapping.
 *
 *  Tala's guided flow (P16-T06) asks a new keeper what they want to track, offers a
 *  handful of clickable choices, and turns the pick into a *proposed* starter domain
 *  they can edit before committing. The proposal's reliable floor is the T10 category
 *  catalog (domains/catalog.py, surfaced at GET /domains/catalog) — the same curated
 *  vocabulary the domain builder rail suggests from, so onboarding and the builder never
 *  drift. Everything here is pure (no DOM, no I/O) so the mapping is unit-tested directly.
 */

import type { CatalogSpring, DomainCatalog, DomainDraft, EgoRelationKind, EgoRelationSpec } from '@/api/domains'

/** One clickable answer to "What do you want to track?". `categoryKey` ties the choice to a
 *  catalog category (the vocabulary source); null marks the "something else" blank-canvas path. */
export interface OnboardingChoice {
  key: string
  label: string
  /** Catalog category this maps to, or null for the custom / blank-canvas choice. */
  categoryKey: string | null
  /** Starter name proposed for this choice, used unless the keeper typed their own. */
  starterName: string
  /** One-line hint shown under the chip. */
  blurb: string
}

/** The choices Tala offers. Order is display order. Keys are stable ids. The five curated
 *  choices map onto catalog categories; "custom" is the escape hatch to a blank canvas. */
export const ONBOARDING_CHOICES: OnboardingChoice[] = [
  {
    key: 'people-crm',
    label: 'People / CRM',
    categoryKey: 'people-crm',
    starterName: 'My Network',
    blurb: 'People, companies, and who knows whom.',
  },
  {
    key: 'media',
    label: 'Media',
    categoryKey: 'media',
    starterName: 'Media Library',
    blurb: 'Titles, creators, studios — what you follow.',
  },
  {
    key: 'markets',
    label: 'Markets',
    categoryKey: 'markets',
    starterName: 'My Watchlist',
    blurb: 'Companies, tickers, theses, positions.',
  },
  {
    key: 'govcon',
    label: 'GovCon',
    categoryKey: 'govcon',
    starterName: 'GovCon Pipeline',
    blurb: 'Opportunities, agencies, awards, vendors.',
  },
  {
    key: 'research',
    label: 'Research',
    categoryKey: 'research',
    starterName: 'Research Notes',
    blurb: 'Papers, authors, topics, datasets.',
  },
  {
    key: 'custom',
    label: 'Something else',
    categoryKey: null,
    starterName: 'My Domain',
    blurb: "Start from a blank canvas — you'll shape it.",
  },
]

export function choiceByKey(key: string): OnboardingChoice | undefined {
  return ONBOARDING_CHOICES.find((c) => c.key === key)
}

// A proposal is deliberately *small* — a handful of types, not the category's whole vocabulary.
// The keeper edits from here, so we seed just enough to feel oriented, never overwhelmed.
const MAX_ENTITY_TYPES = 5
const MAX_EDGE_TYPES = 4
const MAX_EGO_RELATIONS = 3

// The blank-canvas seed for "something else": a light, category-neutral starting point so the
// review screen is never empty (the create endpoint needs ≥1 entity type) yet biases nothing.
// The single `note` ego (an event) mirrors the builder's default row.
const CUSTOM_SEED = {
  entity_types: ['person', 'item', 'note'],
  relation_types: ['relates_to'],
  ego_relations: [{ name: 'note', kind: 'event' }],
} as const

// Prefer a live spring, then idle, then neutral, then error — a name-sorted tiebreak keeps it
// deterministic. Unknown liveness sinks to the bottom.
const LIVENESS_RANK: Record<string, number> = { live: 0, idle: 1, neutral: 2, error: 3 }

/** The single spring to suggest for a category — the healthiest one, deterministically. */
export function pickSuggestedSpring(springs: CatalogSpring[]): string | null {
  if (springs.length === 0) return null
  const best = [...springs].sort((a, b) => {
    const rank = (LIVENESS_RANK[a.liveness] ?? 9) - (LIVENESS_RANK[b.liveness] ?? 9)
    return rank !== 0 ? rank : a.name.localeCompare(b.name)
  })[0]
  return best.name
}

/** One Me → entity relation on the proposal — the kind travels with the name so applied
 *  suggestions (and catalog seeds) never make the keeper choose event/state raw. */
export interface ProposedEgoRelation {
  name: string
  kind: EgoRelationKind
}

/** The editable starter domain Tala proposes from a choice. */
export interface ProposedDomain {
  name: string
  description: string
  /** Catalog category the vocabulary came from, or null for the blank-canvas seed. */
  categoryKey: string | null
  entity_types: string[]
  relation_types: string[]
  /** Me → entity relations — seeded from the category, grown by applied Tala suggestions. */
  ego_relations: ProposedEgoRelation[]
  /** A spring the keeper can attach *after* creation (attachment lives on the domain's inspect
   *  panel, not the create call) — surfaced as a "next step" nudge, null when none fits. */
  suggestedSpring: string | null
}

/**
 * Map a choice (+ optional typed name) to a proposed starter domain, using the live catalog for
 * the vocabulary floor. An unknown choice or the "custom" choice yields the neutral seed. A
 * missing/empty catalog degrades gracefully (empty type lists, null spring) rather than throwing —
 * the keeper can still name-and-create, and the builder rail fills vocabulary in later.
 */
export function proposeDomain(
  choiceKey: string,
  catalog: DomainCatalog | undefined,
  freeText?: string,
): ProposedDomain {
  const choice = choiceByKey(choiceKey)
  const typed = (freeText ?? '').trim()
  const name = typed || choice?.starterName || 'My Domain'

  // Custom, unknown, or category-less → the neutral blank-canvas seed.
  if (!choice || choice.categoryKey == null) {
    return {
      name,
      description: '',
      categoryKey: null,
      entity_types: [...CUSTOM_SEED.entity_types],
      relation_types: [...CUSTOM_SEED.relation_types],
      ego_relations: CUSTOM_SEED.ego_relations.map((e) => ({ ...e })),
      suggestedSpring: null,
    }
  }

  const category = catalog?.categories.find((c) => c.key === choice.categoryKey)
  return {
    name,
    description: '',
    categoryKey: choice.categoryKey,
    entity_types: (category?.entity_types ?? []).slice(0, MAX_ENTITY_TYPES),
    relation_types: (category?.edge_types ?? []).slice(0, MAX_EDGE_TYPES),
    ego_relations: (category?.ego_relations ?? [])
      .slice(0, MAX_EGO_RELATIONS)
      .map((e) => ({ name: e.name, kind: e.kind })),
    suggestedSpring: pickSuggestedSpring(category?.springs ?? []),
  }
}

/** Turn an (edited) proposal into the POST /domains body. The suggested spring is intentionally
 *  NOT sent — springs attach on the domain's inspect panel after it exists, never at create.
 *  Ego relations become the POST map only when the proposal carries some. */
export function draftFromProposed(p: {
  name: string
  description: string
  entity_types: string[]
  relation_types: string[]
  ego_relations?: ProposedEgoRelation[]
}): DomainDraft {
  const draft: DomainDraft = {
    name: p.name.trim(),
    description: p.description.trim(),
    entity_types: p.entity_types,
    relation_types: p.relation_types,
  }
  if (p.ego_relations && p.ego_relations.length > 0) {
    const ego: Record<string, EgoRelationSpec> = {}
    for (const e of p.ego_relations) ego[e.name] = { kind: e.kind }
    draft.ego_relations = ego
  }
  return draft
}

// ── identity replay (the "about you" step, second time around) ────────────────────────────────

/** Flat prefill fields read from an ego node's attributes.identity — tolerant of any junk shape
 *  (an old write, a hand-edited node): a bad tier or field degrades to empty, never throws. */
export interface IdentityFieldValues {
  name: string
  tagline: string
  summary: string
  expertise: string[]
  goals: string[]
}

function tier(identity: unknown, key: string): Record<string, unknown> {
  if (typeof identity !== 'object' || identity === null || Array.isArray(identity)) return {}
  const t = (identity as Record<string, unknown>)[key]
  if (typeof t !== 'object' || t === null || Array.isArray(t)) return {}
  return t as Record<string, unknown>
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function strList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.length > 0) : []
}

export function identityFields(identity: unknown): IdentityFieldValues {
  const t0 = tier(identity, 't0')
  const t1 = tier(identity, 't1')
  const t3 = tier(identity, 't3')
  return {
    name: str(t0.name),
    tagline: str(t0.tagline),
    summary: str(t1.professional_summary),
    expertise: strList(t1.expertise),
    goals: strList(t3.personal_goals),
  }
}

/**
 * Merge freshly-answered tiers onto the CURRENT identity, read-modify-write style. The entity
 * PATCH replaces the `identity` attribute key wholesale, so the client must merge first — a
 * partial replay (name only) must never wipe the other tiers. Tier-level: each updated tier
 * overlays its fields onto the existing tier; untouched tiers (and unknown keys) pass through.
 */
export function mergeIdentity(current: unknown, updates: Record<string, unknown>): Record<string, unknown> {
  const base: Record<string, unknown> =
    typeof current === 'object' && current !== null && !Array.isArray(current)
      ? { ...(current as Record<string, unknown>) }
      : {}
  for (const [key, value] of Object.entries(updates)) {
    const cur = base[key]
    if (
      typeof cur === 'object' && cur !== null && !Array.isArray(cur) &&
      typeof value === 'object' && value !== null && !Array.isArray(value)
    ) {
      base[key] = { ...(cur as Record<string, unknown>), ...(value as Record<string, unknown>) }
    } else {
      base[key] = value
    }
  }
  return base
}
